<template>
  <div>
    <bj-crud-form-modal
      ref="crudModal"
      model="User"
      endpoint-create="admin/agents"
      endpoint-edit="users"
      :fields="fields"
      :edit-fields="editFields"
      :date-fields="dateFields"
      :callback="beforeSave"
      not-crud
      @saved="$refs.dataTable.refresh()"
    />
    <addAgentsDialog
      v-if="addAgentsDialog.visible"
      :add-agent-dialog="addAgentsDialog"
      @close="addAgentsDialog.visible = false"
    />
    <q-btn
      size="md"
      rounded
      outline
      color="positive"
      icon="mail"
      label="Add agents from CSV"
      class="q-mb-md"
      @click.native="addAgentsDialog.visible = true"
    />
    <div class="row full-row-exactly">
      <div class="col-12 flex-1">
        <div
          class="gutter-sm"
          :class="$q.screen.lt.md ? 'column' : 'row'"
        >
          <div
            v-if="$q.screen.lt.md || showFiltersDesktop"
            :class="$q.screen.lt.md && !showFiltersMobile ? 'col-auto' : 'col-md-3'"
          >
            <q-card :class="$q.screen.gt.sm ? 'full-height' : ''">
              <q-card-title>
                <div class="row">
                  <div class="col">
                    Filters
                  </div>
                  <div
                    v-if="$q.screen.lt.md"
                    class="col-auto q-mr-auto"
                  >
                    <q-btn
                      color="secondary"
                      flat
                      round
                      :icon="`keyboard_arrow_${showFiltersMobile ? 'up' :'down'}`"
                      @click="toggleFiltersMobile"
                    />
                  </div>
                </div>
              </q-card-title>
              <q-card-main
                v-if="$q.screen.gt.sm || showFiltersMobile"
                style="height: calc(100% - 64px)"
              >
                <component
                  :is="$q.screen.gt.sm ? 'q-scroll-area' : 'div'"
                  :class="{'full-height': $q.screen.gt.sm}"
                >
                  <bj-filters
                    ref="filtersModal"
                    :key="filtersResetReRenderKey"
                    v-model="filters"
                    :fields="filterFields"
                    @reset="filtersResetReRenderKey++"
                    @input="initFilters"
                  />
                </component>
              </q-card-main>
            </q-card>
          </div>
          <div class="col">
            <bj-data-table
              ref="dataTable"
              :with-cell-delete-button="false"
              :with-cell-edit-button="false"
              add-button-label="Add Agent"
              :columns="columns"
              model="admin/agents"
              many-name="Agents"
              single-name="Agent"
              :query-params="queryParams"
              filter-button
              @toggle-filters="toggleFiltersDesktop"
              @add="$refs.crudModal.open('add', newAgentPayload)"
            >
              <template
                slot="cell-first_name"
                slot-scope="{row}"
              >
                <router-link :to="{ name: 'app.agents.profile', params: { id: row.id } }">
                  {{ row.full_name }}
                </router-link>
                <q-chip
                  v-if="row.is_showami"
                  dense
                  class="ml-2"
                  color="orange"
                  text-color="white"
                >
                  Showami
                </q-chip>
              </template>
              <template
                slot="cell-admin_notes"
                slot-scope="{row}"
              >
                <span
                  v-if="row.admin_notes"
                  :title="row.admin_notes"
                >
                  {{ row.admin_notes.substring(0, 25) + (row.admin_notes.length > 25 ? '...' : '') }}
                </span>
              </template>
              <template
                slot="cell-muted_until"
                slot-scope="{row}"
              >
                <q-chip
                  v-if="row.muted_until"
                  small
                  color="negative"
                >
                  Muted until
                  {{ moment(row.muted_until).tz((row.markets || []).length ? row.markets[0].timezone : null).format('MM/DD/YY h:mmA') }}
                  <q-icon
                    v-if="(row.markets || []).length"
                    v-tippy
                    class="ml-1"
                    name="access_time"
                    size="14px"
                    :title="row.markets[0].timezone"
                  />
                </q-chip>
              </template>
              <template
                slot="cell-blocked"
                slot-scope="{row}"
              >
                <q-checkbox
                  v-model="row.blocked"
                  @input="blockAgent(row)"
                />
              </template>
              <template
                slot="cell-location"
                slot-scope="{row}"
              >
                <a
                  v-if="row.location"
                  v-tippy
                  :href="`https://www.google.com/maps/search/?api=1&query=${row.location.coordinates[1]},${row.location.coordinates[0]}`"
                  target="_blank"
                  title="Open google maps"
                >
                  {{ row.location.coordinates[1] }},
                  {{ row.location.coordinates[0] }}
                </a>
              </template>
              <template
                slot="cell-stripe"
                slot-scope="{row}"
              >
                <span
                  v-if="row.stripe == null"
                  class="text-negative"
                >Not registered</span>
                <span v-if="row.stripe != null && row.stripe.data != undefined && row.stripe.data.stripe_user_id != undefined">{{ row.stripe.data.stripe_user_id }}</span>
              </template>
              <template
                slot="cell-used_app_at"
                slot-scope="{row}"
              >
                <span v-if="row.used_app_at">
                  <SdDatetime :value="row.used_app_at" />
                </span>
              </template>
              <template
                slot="cell-device"
                slot-scope="{row}"
              >
                <span v-if="row.device">
                  <q-icon
                    v-if="typeof row.device.platform === 'string' && row.device.platform.toLowerCase() === 'android'"
                    v-tippy
                    name="fab fa-android"
                    color="green"
                    title="Android"
                  />
                  <q-icon
                    v-else-if="typeof row.device.platform === 'string' && row.device.platform.toLowerCase() === 'ios'"
                    v-tippy
                    name="fab fa-apple"
                    title="iOS"
                  />
                  {{ row.device.model }}
                  <span v-if="row.device.manufacturer">{{ row.device.manufacturer.charAt(0).toUpperCase() + row.device.manufacturer.slice(1) }}</span>
                </span>
              </template>
              <template
                slot="cell-contact_status"
                slot-scope="{row}"
              >
                <contact-status-dropdown
                  :ref="`status_${row.id}`"
                  :current="row.contact_status"
                  @change="(state)=>{updateContactStatus(row, state)}"
                />
              </template>
              <template
                slot="cell-contact_status_updated_at"
                slot-scope="{row}"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="col-auto">
                    <SdDatetime
                      v-if="row.contact_status_updated_at"
                      class="d-inline-flex"
                      :value="row.contact_status_updated_at"
                      :show-icon="false"
                      format="MM/DD/YY"
                    />
                    <agent-contact-status-updated-at-date-picker
                      :contact-status-updated-at="row.contact_status_updated_at"
                      :agent="row"
                      @dates-updated="(dates)=>updateContactStatusUpdatedAt(row, dates)"
                    />
                    <q-icon
                      v-if="row.contact_status_updated_at"
                      name="close"
                      size="16px"
                      color="primary"
                      class="ml-1 cursor-pointer"
                      @click.native="()=>clearContactStatusUpdatedAt(row)"
                    />
                  </div>
                </div>
              </template>
              <template
                slot="cell-markets"
                slot-scope="{row}"
              >
                {{ (row.markets || []).map(m => m.name).join(', ') }}
              </template>
              <template
                slot="cell-is_periodic_inspection_capable"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.is_periodic_inspection_capable ? 'positive' : 'negative'"
                >
                  {{ row.is_periodic_inspection_capable ? 'Capable' : 'Incapable' }}
                </q-chip>
              </template>
              <template
                slot="cell-is_mimo_inspection_capable"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.is_mimo_inspection_capable ? 'positive' : 'negative'"
                >
                  {{ row.is_mimo_inspection_capable ? 'Capable' : 'Incapable' }}
                </q-chip>
              </template>
              <template
                slot="cell-is_vacancy_check_inspection_capable"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.is_vacancy_check_inspection_capable ? 'positive' : 'negative'"
                >
                  {{ row.is_vacancy_check_inspection_capable ? 'Capable' : 'Incapable' }}
                </q-chip>
              </template>
              <template
                slot="cell-is_marketing_photos_capable"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.is_marketing_photos_capable ? 'positive' : 'negative'"
                >
                  {{ row.is_marketing_photos_capable ? 'Capable' : 'Incapable' }}
                </q-chip>
              </template>
              <template
                slot="cell-is_tours_capable"
                slot-scope="{row}"
              >
                <q-chip
                  dense
                  :color="row.is_tours_capable ? 'positive' : 'negative'"
                >
                  {{ row.is_tours_capable ? 'Capable' : 'Incapable' }}
                </q-chip>
              </template>
              <template
                slot="cell-actions"
                slot-scope="{row}"
              >
                <q-btn
                  size="sm"
                  rounded
                  label="Edit"
                  outline
                  color="black"
                  icon="edit"
                  class="q-mr-sm"
                  @click.native="openEditModal(row)"
                />
                <q-btn
                  v-if="!row.verified"
                  :disable="disabled"
                  size="sm"
                  rounded
                  outline
                  color="positive"
                  icon="mail"
                  label="Resend Invite"
                  class="q-ml-sm"
                  @click.native="sendVerificationEmail(row.id)"
                />
              </template>
            </bj-data-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectInspectionAgent from '@/components/select-inspection-agent.vue';
import InspectionScheduleDatePicker from '@/components/inspection-schedule-date-picker.vue';
import SdDatetime from '@/plugins/bluejay/components/datetime.vue';
import config from '@/plugins/bluejay/config';
import AgentContactStatusUpdatedAtDatePicker from '@/components/agent-contact-status-updated-at-date-picker.vue';
import AddAgentsDialog from './agent/addAgentsDialog';
import BjFilters from '../../components/filters';
import ContactStatusDropdown from '../../components/contact-status-dropdown';

const { contactStatuses } = config;

export default {
  components: {
    AgentContactStatusUpdatedAtDatePicker,
    SdDatetime,
    InspectionScheduleDatePicker,
    SelectInspectionAgent,
    BjFilters,
    AddAgentsDialog,
    ContactStatusDropdown,
  },
  data() {
    const fields = [
      {
        name: 'first_name',
        type: 'text',
        label: 'First Name',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'last_name',
        type: 'text',
        label: 'Last Name',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'email',
        type: 'email',
        label: 'Email',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'phone_number',
        type: 'text',
        label: 'Phone Number',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'contact_status',
        type: 'select',
        label: 'Greeting Call',
        class: 'col-xs-12 col-md-6',
        clearable: true,
        optionsResolver: () => Promise.resolve(Object.values(contactStatuses).map((contactStatus) => ({
          label: contactStatus.label,
          value: contactStatus.value,
        }))),
      },
      {
        name: 'contact_status_updated_at',
        type: 'date',
        label: 'Greeting call updated at',
        class: 'col-xs-12 col-md-6',
        utc: true,
        clearable: true,
      },
      {
        name: 'markets',
        type: 'chips-autocomplete',
        label: 'Markets',
        class: 'col-xs-12',
        placeholder: 'Add a market...',
        search: (query) => this.markets
          .filter((market) => market.name.toLowerCase().includes(query.toLowerCase())),
        toLabel: (market) => `${market.name} #${market.id}`,
        rules: ['required'],
      },
      {
        name: 'is_periodic_inspection_capable',
        type: 'toggle',
        label: 'Periodic inspections capable',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'is_mimo_inspection_capable',
        type: 'toggle',
        label: 'Move-in/Out inspections capable',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'is_vacancy_check_inspection_capable',
        type: 'toggle',
        label: 'Vacancy check inspections capable',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'is_marketing_photos_capable',
        type: 'toggle',
        label: 'Marketing Photos capable',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        class: 'col-xs-12',
      },
      {
        name: 'is_tours_experienced',
        type: 'toggle',
        label: 'Tours Experience',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'is_inspections_experienced',
        type: 'toggle',
        label: 'Inspections Experience',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        name: 'is_tours_capable',
        type: 'toggle',
        label: 'Tours capable',
        rules: ['required'],
        class: 'col-xs-12 col-md-6',
      },
      {
        class: 'col-xs-12',
      },
      {
        name: 'admin_notes',
        type: 'custom-textarea',
        label: 'Notes',
        class: 'col-xs-12 col-md-6',
        placeholder: 'Enter notes regarding agent',
      },
    ];

    const mutedUntilField = {
      name: 'muted_until',
      type: 'date',
      label: 'Muted until',
      class: 'col-xs-12 col-md-6',
      clearable: true,
      min: new Date(),
      utc: true,
    };

    return {
      mutedUntilField,
      queryParams: {
        id: this.$route.params.id ? [this.$route.params.id] : null,
        scopes: {},
      },
      showFiltersMobile: false,
      showFiltersDesktop: false,
      filters: {
        markets: [],
        last_app_usage_from: null,
        last_app_usage_to: null,
        is_periodic_inspection_capable: null,
        is_mimo_inspection_capable: null,
        is_vacancy_check_inspection_capable: null,
        is_marketing_photos_capable: null,
      },
      filterFields: [
        'markets',
        'last_app_usage_date',
        'is_periodic_inspection_capable',
        'is_mimo_inspection_capable',
        'is_vacancy_check_inspection_capable',
        'is_marketing_photos_capable',
      ],
      columns: [
        {
          name: 'id',
          field: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          name: 'first_name',
          field: 'full_name',
          label: 'Full Name',
          sortable: false,
        },
        {
          name: 'email',
          field: 'email',
          label: 'Email',
          sortable: true,
        },
        {
          name: 'formatted_phone_number',
          field: 'formatted_phone_number',
          label: 'Phone Number',
          sortable: false,
        },
        {
          name: 'admin_notes',
          field: 'admin_notes',
          label: 'Notes',
          sortable: false,
        },
        {
          name: 'muted_until',
          field: 'muted_until',
          label: 'Muted Notifications',
          sortable: true,
        },
        {
          name: 'blocked',
          field: 'blocked',
          label: 'Blocked',
          sortable: true,
        },
        {
          name: 'location',
          field: 'location',
          label: 'Location',
          sortable: false,
        },
        {
          name: 'stripe',
          field: 'stripe',
          label: 'Stripe account',
          sortable: false,
        },
        {
          name: 'used_app_at',
          field: 'used_app_at',
          label: 'Last app usage',
          sortable: true,
        },
        {
          name: 'tours_count',
          field: 'tours_count',
          label: 'Tours count',
          sortable: false,
        },
        {
          name: 'inspections_count',
          field: 'inspections_count',
          label: 'Condition reports count',
          sortable: false,
        },
        {
          name: 'device',
          field: 'device',
          label: 'Device',
          sortable: false,
        },
        {
          name: 'app_version',
          field: 'app_version',
          label: 'App version',
          sortable: true,
        },
        {
          name: 'markets',
          field: 'markets',
          label: 'Markets',
          sortable: false,
        },
        {
          name: 'contact_status',
          field: 'contact_status',
          label: 'Greeting Call',
          sortable: false,
        },
        {
          name: 'contact_status_updated_at',
          field: 'contact_status_updated_at',
          label: 'Greeting Call Updated At',
          sortable: false,
        },
        {
          name: 'is_periodic_inspection_capable',
          field: 'is_periodic_inspection_capable',
          label: 'Periodic inspections capable',
          sortable: true,
        },
        {
          name: 'is_mimo_inspection_capable',
          field: 'is_mimo_inspection_capable',
          label: 'Move-in/Out inspections capable',
          sortable: true,
        },
        {
          name: 'is_vacancy_check_inspection_capable',
          field: 'is_vacancy_check_inspection_capable',
          label: 'Vacancy check inspections capable',
          sortable: true,
        },
        {
          name: 'is_marketing_photos_capable',
          field: 'is_marketing_photos_capable',
          label: 'Marketing Photos capable',
          sortable: true,
        },
        {
          name: 'is_tours_capable',
          field: 'is_tours_capable',
          label: 'Tours capable',
          sortable: true,
        },
      ],
      fields,
      editingAgent: null,
      dateFields: ['muted_until'],
      newAgentPayload: {
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        markets: [],
        is_periodic_inspection_capable: false,
        is_mimo_inspection_capable: false,
        is_vacancy_check_inspection_capable: false,
        is_marketing_photos_capable: false,
        is_tours_experienced: false,
        is_inspections_experienced: false,
        is_tours_capable: true,
      },
      markets: [],
      addAgentsDialog: this.clone({
        visible: false,
        loading: false,
      }),
      disabled: false,
      filtersResetReRenderKey: 1,
    };
  },
  computed: {
    editFields() {
      const baseFields = [...this.fields];
      const insertIndex = baseFields.findIndex((f) => f.name === 'is_tours_capable') + 1;
      const fieldsWithMuted = baseFields.toSpliced(insertIndex, 0, this.mutedUntilField);
      const isShowami = Boolean(this.editingAgent && this.editingAgent.is_showami);

      return fieldsWithMuted.map((field) => {
        if (field.name === 'markets' && isShowami) {
          return { ...field, condition: () => false };
        }
        return field;
      });
    },
  },
  created() {
    [this.queryParams, this.filters] = this.applySortingAndFiltering(this.queryParams, this.filters);
    this.fetchMarkets();
  },
  mounted() {
    this.$root.$on('fetched', (queryParams) => {
      if (this.queryParams !== null && (queryParams.page != this.queryParams.page
          || queryParams.limit != this.queryParams.limit)) {
        this.queryParams.page = queryParams.page;
        this.queryParams.limit = queryParams.limit;
      }
      this.addParamsToLocation(queryParams);
      this.addParamsToLocation(queryParams);
    });
  },
  methods: {
    blockAgent(agent) {
      const url = `${agent.blocked ? '' : 'un'}block-agent/${agent.id}`;
      this.api().request('POST', url).then((response) => {
        const agent_state = response.blocked ? 'blocked' : 'unblocked';
        this.uiNotify(`Agent ${agent_state} successfully`, 'positive');
        this.$refs.dataTable.refresh();
      }).catch(() => {
        this.uiNotify('Error blocking agent', 'negative');
      });
    },
    sendVerificationEmail(id) {
      this.disabled = true;
      this.uiConfirm('Are you sure?').then(() => {
        this.api().request('POST', `users/${id}/send-invitation-mail`).then(() => {
          this.uiNotify('Verification email sent.');
        }).catch(() => {
          this.uiNotify('Error', 'negative');
        })
          .finally(() => {
            this.disabled = false;
          });
      });
    },
    openEditModal(row) {
      this.editingAgent = row;
      this.$refs.crudModal.open('edit', this.preparePayloadToEdit(row));
    },
    preparePayloadToEdit(payload) {
      if (payload.muted_until) {
        payload.muted_until = this.moment(payload.muted_until)
          .tz(payload.markets && payload.markets.length ? payload.markets[0].timezone : null)
          .format('YYYY-MM-DD');
      }

      return {
        ...payload,
        location: undefined,
        markets: payload.markets || [],
      };
    },
    fetchMarkets() {
      this.api().request('GET', 'admin/markets', {}, {
        paginate: 0,
      }).then((markets) => this.markets = markets);
    },
    beforeSave(payload) {
      const markets = this.editingAgent && this.editingAgent.is_showami
        ? []
        : (payload.markets || []).map((market) => (typeof market === 'object' && market && 'id' in market ? market.id : market));
      return {
        ...payload,
        markets,
      };
    },
    toggleFiltersMobile() {
      this.showFiltersMobile = !this.showFiltersMobile;
    },
    toggleFiltersDesktop() {
      this.showFiltersDesktop = !this.showFiltersDesktop;
    },
    initFilters() {
      Object.keys(this.filters).forEach((key) => {
        const filter = this.clone(this.filters[key]);

        if (filter && filter.length) {
          this.queryParams[key] = filter;
        } else {
          delete this.queryParams[key];
        }
      });
      setTimeout(() => {
        this.$refs.dataTable.refresh();
      }, 100);
    },
    updateContactStatus(user, status) {
      this.updateUserData(user, { contact_status: status }, 'Error occurred while updating greeting call');
    },
    clearContactStatusUpdatedAt(user) {
      this.updateUserData(user, { contact_status_updated_at: null }, 'Error occurred while clearing greeting call updated at');
    },
    updateContactStatusUpdatedAt(user, data) {
      this.updateUserData(user, data, 'Error occurred while updating greeting call updated at');
    },
    async updateUserData(user, data, errorMessage) {
      try {
        await this.api().request('PATCH', `users/${user.id}`, data);
      } catch (error) {
        this.uiNotify(errorMessage, 'negative');
      }
      this.$refs.dataTable.refresh();
    },
  },
};
</script>
